import { FetchState } from "../../entities/FetchState";
import { IReportDataObject, emptyReportDataObject } from "../../entities/reports/IReportData";
import { Reducer } from "redux";
import { all, call, fork, put, takeEvery } from "redux-saga/effects";
import { GraphQl, IError } from "../../utils/callGraphQl";
import Either from "../../utils/Either";
import { ActionCreators as Navigation } from "../navigate"
import {callApiNoResult, callApiWithResult, ICallNoResult, ICallResult} from "../../utils/callApi";

/**********************************************************
 * API Data
 **********************************************************/
export interface IDataDoelgroep {
    doelgroepId: string;
    doelgroep: string;
}
export interface IDataMedewerker {
    medewerkerId: string;
    medewerker: string; 
}
export interface IData {
    id: string;
    medewerkers: IDataMedewerker[];
    datum: Date;
    contacts: number;
    organisatie: string;
    doelgroepen: IDataDoelgroep[];
    doelgroepDetail: string;
    thema: string;
    aantalAanwezigProfessioneel: number;
    aantalAanwezigNietProfessioneel: number;
    savedInGir: boolean;
    locked: boolean;
}

export const defaultOutreachItem: IData = {
    id: crypto.randomUUID(),
    aantalAanwezigNietProfessioneel: 0,
    aantalAanwezigProfessioneel: 0,
    datum: new Date(),
    contacts: 1,
    doelgroepen: [],
    doelgroepDetail: '',
    medewerkers: [],
    organisatie: '',
    savedInGir: false,
    thema: '',
    locked: false
};

// Make a default OutreachDetail with empty values and a date of today
export const defaultOutreachDetail= emptyReportDataObject<IData>(defaultOutreachItem)

/**********************************************************
 * API Settings
 **********************************************************/

const graphQlAddSelector = `id`

interface IGraphQlQueryAddResponse {
    company: {
        addOutreachItem: {
            id: string
        }
    }
}

interface IGraphQlQueryUpdateResponse {
    company: {
        updateOutreachItem: {
            id: string
        }
    }
}

interface IGraphQlQueryDeleteResponse {
    company: {
        deleteOutreachItem: string
    }
}

/**********************************************************
 * State
 **********************************************************/

export interface IState {
    Item: IReportDataObject<IData>
    AddItem: IReportDataObject<IData>
    UpdateItem: IReportDataObject<IData>
    DeleteItem: IReportDataObject<IData>
}
const InitialState: IState = {
    Item: defaultOutreachDetail,
    AddItem: defaultOutreachDetail,
    UpdateItem: defaultOutreachDetail,
    DeleteItem: defaultOutreachDetail
}

/**********************************************************
 * Action Types
 **********************************************************/
export enum ActionTypes {
    // Fetch action types
    FetchData = "outreach.OutreachItem Fetch",
    FetchDataSuccess = "outreach.OutreachItem FetchSuccess",
    FetchDataFailed = "outreach.OutreachItem FetchFailed",
    // Add action types
    AddData = "outreach.OutreachItem Add",
    AddDataSuccess = "outreach.OutreachItem AddSuccess",
    AddDataFailed = "outreach.OutreachItem AddFailed",
    // Update action types
    UpdateData = "outreach.OutreachItem Update",
    UpdateDataSuccess = "outreach.OutreachItem UpdateSuccess",
    UpdateDataFailed = "outreach.OutreachItem UpdateFailed",
    // Delete action types
    DeleteData = "outreach.OutreachItem Delete",
    DeleteDataSuccess = "outreach.OutreachItem DeleteSuccess",
    DeleteDataFailed = "outreach.OutreachItem DeleteFailed"
}

/**********************************************************
 * Actions & Creators
 **********************************************************/
export interface IFetchData {
    type: ActionTypes.FetchData,
    payload: { id: string }
}
export interface IFetchDataSuccess {
    type: ActionTypes.FetchDataSuccess,
    payload: { data: IData }
}
export interface IFetchDataFailed {
    type: ActionTypes.FetchDataFailed,
    payload: { errorMessage: string }
}
export interface IAddData {
    type: ActionTypes.AddData,
    payload: { data: IData }
}
export interface IAddDataSuccess {
    type: ActionTypes.AddDataSuccess,
    payload: { id: string }
}
export interface IAddDataFailed {
    type: ActionTypes.AddDataFailed,
    payload: { errorMessage: string }
}

// Update Actions
export interface IUpdateData {
    type: ActionTypes.UpdateData,
    payload: { data: IData }
}
export interface IUpdateDataSuccess {
    type: ActionTypes.UpdateDataSuccess,
    payload: { id: string }
}
export interface IUpdateDataFailed {
    type: ActionTypes.UpdateDataFailed,
    payload: { errorMessage: string }
}

// Delete Actions
export interface IDeleteData {
    type: ActionTypes.DeleteData,
    payload: { id: string }
}
export interface IDeleteDataSuccess {
    type: ActionTypes.DeleteDataSuccess,
    payload: { id: string }
}
export interface IDeleteDataFailed {
    type: ActionTypes.DeleteDataFailed,
    payload: { errorMessage: string }
}

type Actions = IFetchData | IFetchDataSuccess | IFetchDataFailed | IAddData | IAddDataSuccess | IAddDataFailed | IUpdateData | IUpdateDataSuccess | IUpdateDataFailed | IDeleteData | IDeleteDataSuccess | IDeleteDataFailed;

export class ActionCreators {
    public static FetchData(id: string): IFetchData {
        return { 
            type: ActionTypes.FetchData, 
            payload: { id } 
        };
    }
    public static FetchDataSuccess(newData: IData): IFetchDataSuccess {
        return {
            type: ActionTypes.FetchDataSuccess,
            payload: { data: newData }
        };
    }
    public static FetchDataFailed(err: string): IFetchDataFailed {
        return {
            type: ActionTypes.FetchDataFailed,
            payload: { errorMessage: err }
        };
    }
    public static AddData(data: IData): IAddData {
        return { 
            type: ActionTypes.AddData, 
            payload: { data } 
        };
    }
    public static AddDataSuccess(id: string): IAddDataSuccess {
        return {
            type: ActionTypes.AddDataSuccess,
            payload: { id }
        };
    }
    public static AddDataFailed(err: string): IAddDataFailed {
        return {
            type: ActionTypes.AddDataFailed,
            payload: { errorMessage: err }
        };
    }

    public static UpdateData(data: IData): IUpdateData {
        return { 
            type: ActionTypes.UpdateData, 
            payload: { data } 
        };
    }
    public static UpdateDataSuccess(id: string): IUpdateDataSuccess {
        return {
            type: ActionTypes.UpdateDataSuccess,
            payload: { id }
        };
    }
    public static UpdateDataFailed(err: string): IUpdateDataFailed {
        return {
            type: ActionTypes.UpdateDataFailed,
            payload: { errorMessage: err }
        };
    }

    public static DeleteData(id: string): IDeleteData {
        return { 
            type: ActionTypes.DeleteData, 
            payload: { id } 
        };
    }
    public static DeleteDataSuccess(id: string): IDeleteDataSuccess {
        return {
            type: ActionTypes.DeleteDataSuccess,
            payload: { id }
        };
    }
    public static DeleteDataFailed(err: string): IDeleteDataFailed {
        return {
            type: ActionTypes.DeleteDataFailed,
            payload: { errorMessage: err }
        };
    }
}

/**********************************************************
 * Reducer
 **********************************************************/
export const Reducers: Reducer<IState, Actions> = (state = InitialState, action) => {
    switch (action.type) {
        case ActionTypes.FetchData:
            return { 
                ...state, 
                Item: {
                    ...state.Item,
                    state: FetchState.Busy
                }
            };
        case ActionTypes.FetchDataSuccess:
            return { 
                ...state, 
                Item: {
                    ...state.Item,
                    state: FetchState.Success,
                    data: action.payload.data,
                    fetchDate: new Date()
                }
            };
        case ActionTypes.FetchDataFailed:
            return { 
                ...state, 
                Item: {
                    ...state.Item,
                    state: FetchState.Error,
                    errorMessage: action.payload.errorMessage
                }
            };
        case ActionTypes.AddData:
            return { 
                ...state, 
                AddItem: {
                    ...state.AddItem,
                    state: FetchState.Busy
                }
            };
        case ActionTypes.AddDataSuccess:
            return { 
                ...state, 
                AddItem: {
                    ...state.AddItem,
                    state: FetchState.Success,
                    data: {
                        ... state.AddItem.data,
                        id: action.payload.id
                    },
                    fetchDate: new Date()
                }
            };
        case ActionTypes.AddDataFailed:
            return { 
                ...state, 
                AddItem: {
                    ...state.AddItem,
                    state: FetchState.Error,
                    errorMessage: action.payload.errorMessage
                }
            };

        case ActionTypes.UpdateData:
            return { 
                ...state, 
                UpdateItem: {
                    ...state.UpdateItem,
                    state: FetchState.Busy
                }
            };
        case ActionTypes.UpdateDataSuccess:
            return { 
                ...state, 
                UpdateItem: {
                    ...state.UpdateItem,
                    state: FetchState.Success,
                    data: {
                        ... state.UpdateItem.data,
                        id: action.payload.id
                    },
                    fetchDate: new Date()
                },
                Item: {
                    ...InitialState.Item,
                }
            };

        case ActionTypes.UpdateDataFailed:
            return { 
                ...state, 
                UpdateItem: {
                    ...state.UpdateItem,
                    state: FetchState.Error,
                    errorMessage: action.payload.errorMessage
                }
            };

        case ActionTypes.DeleteData:
            return { 
                ...state, 
                DeleteItem: {
                    ...state.DeleteItem,
                    state: FetchState.Busy
                },
                Item: {
                    ...InitialState.Item,
                }
            };

        case ActionTypes.DeleteDataSuccess:
            return { 
                ...state, 
                DeleteItem: {
                    ...state.DeleteItem,
                    state: FetchState.Success,
                    data: {
                        ... state.DeleteItem.data,
                        id: action.payload.id
                    },
                    fetchDate: new Date()
                }
            };

        case ActionTypes.DeleteDataFailed:
            return { 
                ...state, 
                DeleteItem: {
                    ...state.DeleteItem,
                    state: FetchState.Error,
                    errorMessage: action.payload.errorMessage
                }
            };

        default:
            return state;
    }
}

/**********************************************************
 * Sagas
 **********************************************************/

function* handleIFetchData(action: IFetchData) {
    try {
        const response : Either<IError, ICallResult<IData>> = yield call(callApiWithResult, 'GET', `/v1/outreach/${action.payload.id}`);
        yield response
            .FlatMap(success => put(ActionCreators.FetchDataSuccess(success.result) as any))
            .Reduce(error => put(ActionCreators.FetchDataFailed(error.text) as any))
    } catch (err) {
        console.error('OutreachItems.handleIFetchData: ', err, action)
        yield put(ActionCreators.FetchDataFailed(JSON.stringify(err)))
    }
}

function* handleIAddData(action: IAddData) {
    try {
        const outreachUpdateDto = {
            id: crypto.randomUUID(),
            medewerkerIds: action.payload.data.medewerkers.map(m => m.medewerkerId),
            datum: action.payload.data.datum.toISOString(),
            contacts: action.payload.data.contacts,
            organisatie: action.payload.data.organisatie,
            doelgroepIds: action.payload.data.doelgroepen.map(d => d.doelgroepId),
            doelgroepDetails: action.payload.data.doelgroepDetail,
            thema: action.payload.data.thema,
            aantalAanwezigProfessioneel: action.payload.data.aantalAanwezigProfessioneel,
            aantalAanwezigNietProfessioneel: action.payload.data.aantalAanwezigNietProfessioneel
        };


        const response : Either<IError, ICallNoResult> = yield call(callApiNoResult, 'PUT', `/v1/outreach/${outreachUpdateDto.id}`, outreachUpdateDto);
        yield response
            .FlatMap(success => put(ActionCreators.AddDataSuccess(outreachUpdateDto.id) as any))
            .Reduce(error => put(ActionCreators.AddDataFailed(error.text) as any))
    } catch (err) {
        yield put(ActionCreators.AddDataFailed(JSON.stringify(err)))
    }
}

function* handleIUpdateData(action: IAddData) {
    try {

        const outreachUpdateDto = {
            id: action.payload.data.id,
            medewerkerIds: action.payload.data.medewerkers.map(m => m.medewerkerId),
            datum: action.payload.data.datum.toISOString(),
            contacts: action.payload.data.contacts,
            organisatie: action.payload.data.organisatie,
            doelgroepIds: action.payload.data.doelgroepen.map(d => d.doelgroepId),
            doelgroepDetails: action.payload.data.doelgroepDetail,
            thema: action.payload.data.thema,
            aantalAanwezigProfessioneel: action.payload.data.aantalAanwezigProfessioneel,
            aantalAanwezigNietProfessioneel: action.payload.data.aantalAanwezigNietProfessioneel
        };


        const response : Either<IError, ICallNoResult> = yield call(callApiNoResult, 'PUT', `/v1/outreach/${outreachUpdateDto.id}`, outreachUpdateDto);
        yield response
            .FlatMap(success => put(ActionCreators.UpdateDataSuccess(outreachUpdateDto.id) as any))
            .Reduce(error => put(ActionCreators.UpdateDataFailed(error.text) as any))
    } catch (err) {
        yield put(ActionCreators.UpdateDataFailed(JSON.stringify(err)))
    }
}

function* handleIDeleteData(action: IDeleteData) {
    try {

        const response : Either<IError, ICallNoResult> = yield call(callApiNoResult, 'DELETE', `/v1/outreach/${action.payload.id}`);
        yield response
            .FlatMap(success => put(ActionCreators.DeleteDataSuccess(action.payload.id) as any))
            .Reduce(error => put(ActionCreators.DeleteDataFailed(error.text) as any))
    } catch (err) {
        yield put(ActionCreators.DeleteDataFailed(JSON.stringify(err)))
    }
}

function* handleIAddDataSuccess(action: IAddDataSuccess) {
    yield put(Navigation.ToOutreachDetail(action.payload.id))
}

function* handleIUpdateDataSuccess(action: IUpdateDataSuccess) {
    yield put(Navigation.ToOutreachDetail(action.payload.id))
}

function* handleIDeleteDataSuccess(action: IDeleteDataSuccess) {
    yield put(Navigation.ToOutreach())
}

function* watchAll() {
    yield takeEvery(ActionTypes.FetchData, handleIFetchData)
    yield takeEvery(ActionTypes.AddData, handleIAddData)
    yield takeEvery(ActionTypes.AddDataSuccess, handleIAddDataSuccess)
    yield takeEvery(ActionTypes.UpdateData, handleIUpdateData)
    yield takeEvery(ActionTypes.UpdateDataSuccess, handleIUpdateDataSuccess)
    yield takeEvery(ActionTypes.DeleteData, handleIDeleteData)
    yield takeEvery(ActionTypes.DeleteDataSuccess, handleIDeleteDataSuccess)
}
export function* sagas() {
    yield all([fork(watchAll)]);
}